import { path, pathOr } from 'ramda';
import { createSelector } from 'reselect';

import {
  OperationCellData,
  OperationData,
  PROCESSING_CATEGORIES,
  ProfitItemCellData,
  ProfitItemData,
  DiscountCellData,
  DiscountData,
  AddOnData,
  AddOnCellData,
} from '@frontend/api';

import * as collaboratorSelectors from 'modules/quotes/collaborators/data/selectors';
import { isUserReadOnly as getIsUserReadOnly } from 'modules/quotes/permissions';
import {
  getBriefComponentsById,
  getExpandedComponentId,
  getIsAttachingByPartId,
} from 'modules/quotes/quoteItemComponents/data/reducer';
import {
  getHasMultiComponentNestByQuoteItemId,
  getRootComponentIdByQuoteItemId,
} from 'modules/quotes/quoteItems/data/attributeSelectors/quoteItems';

import { RootState } from 'store/configureStore';
import { getEntities, getEntity, denormalize } from 'store/entities/selectors';
import * as schema from 'store/quotes/schema';
import * as quoteSelectors from 'store/quotes/selectors';
import { getBAQLoadingStatus } from 'store/quotes/selectors';
import * as sessionSelectors from 'store/session/selectors';

import { isStatusInProgress } from 'utils/partUtils';

import { QuoteItemsState } from './reducer';
import { getIsThumbnailReadyFromProcessingStatuses } from './utils/getIsThumbnailReadyFromProcessingStatuses';

const getScope = (state: RootState): QuoteItemsState =>
  state.modules.quotes.quoteItems.table;

export const getAddOn = (
  state: RootState,
  addOnId: number,
): AddOnData | undefined => getEntity(state, 'quoteItemAddOns', addOnId);

export const getIsAddingPurchasedComponent = (state) =>
  getScope(state).isAddingPurchasedComponent;

export const getAddOnCell = (
  state: RootState,
  addOnCellId: number,
): AddOnCellData | undefined =>
  getEntity(state, 'quoteItemAddOnCells', addOnCellId);

export const getPart = (state, partUuid) => getEntity(state, 'parts', partUuid);

export const getCellsForAddOn = (
  state: RootState,
  addOnId: number,
): AddOnCellData[] =>
  Object.keys(getEntities(state, 'quoteItemAddOnCells'))
    .map((cellId) => getEntity(state, 'quoteItemAddOnCells', cellId))
    .filter((cell) => cell.addOnId === addOnId);

export const getExpandedQuoteItemId = (state) =>
  getScope(state).expandedQuoteItem;

export const getQuoteItemById = (state, quoteItemId) =>
  quoteSelectors.getQuoteItemById(state, quoteItemId);

export const getPositionByQuoteItemId = (state, quoteItemId) =>
  getQuoteItemById(state, quoteItemId)?.position;

export const getExpandedQuoteItem = (state) =>
  getQuoteItemById(state, getExpandedQuoteItemId(state));

export const getQuoteItemsById = createSelector(
  [(state) => state.entities.quoteItems, (_, quoteItemIds) => quoteItemIds],
  (quoteItems, quoteItemIds) => {
    if (quoteItemIds.length > 0) {
      return Object.values(quoteItems).filter((quoteItem) =>
        quoteItemIds.includes(parseInt(quoteItem.id)),
      );
    }
    return [];
  },
);

export const getQuoteItemsByIdsDenormalized = (state, quoteItemIds) =>
  denormalize(state, quoteItemIds, schema.quoteItems);

export const getQuoteItemsRespectingQuoteLoadingState = (state) => {
  if (getBAQLoadingStatus(state)) {
    return [];
  } else {
    return quoteSelectors
      .getQuoteItemIds(state)
      ?.map((quoteItemId) => getQuoteItemById(state, quoteItemId));
  }
};

export const getSortedQuoteItemIds = createSelector(
  quoteSelectors.getQuoteItems,
  (quoteItems) => quoteItems.map(({ id }) => id),
);

export const getQuoteItemByIdDenormalized = (state, quoteItemId) =>
  quoteSelectors.getQuoteItemByIdDenormalized(state, quoteItemId);

export const getIsExpandedQuoteItemLocked = (state) =>
  getScope(state).isExpandedQuoteItemLocked;

export const getIsAnotherPersonEditingQuoteItem = (quoteItemId) => (state) =>
  !!collaboratorSelectors.getCollaboratorByQuoteItemId(state, quoteItemId) ||
  (quoteItemId === getExpandedQuoteItemId(state) &&
    getIsExpandedQuoteItemLocked(state));

export const getIsQuoteItemReadOnly = (state, quoteItemId) =>
  quoteSelectors.getIsQuoteReadOnly(state) ||
  getIsAnotherPersonEditingQuoteItem(quoteItemId)(state);

export const getAreQuoteItemsReadOnly = (state, quoteItemIds) =>
  quoteSelectors.getIsQuoteReadOnly(state) ||
  quoteItemIds.some((quoteItemId) =>
    getIsAnotherPersonEditingQuoteItem(quoteItemId)(state),
  );

export const getIsQuoteItemPartDescriptorEditsDisabled =
  (quoteItemId) => (state) => {
    const quoteItem = getQuoteItemById(state, quoteItemId);
    const isAnotherPersonEditingQuoteItem =
      getIsAnotherPersonEditingQuoteItem(quoteItemId)(state);
    return (
      quoteItem?.partIsInAFinalizedQuote ||
      quoteItem?.partIsInATrashedQuote ||
      !quoteItem?.isModifiable ||
      isAnotherPersonEditingQuoteItem
    );
  };

export const getIsRootPartInAFinalizedQuote = (quoteItemId) => (state) =>
  getQuoteItemById(state, quoteItemId)?.partIsInAFinalizedQuote;

export const getIsRootPartInATrashedQuote = (quoteItemId) => (state) =>
  getQuoteItemById(state, quoteItemId)?.partIsInATrashedQuote;

export const getIsRootPartInMultipleQuoteItems = (quoteItemId) => (state) =>
  !getQuoteItemById(state, quoteItemId)?.isModifiable;
export const getIsQuoteItemModifiable = (state, quoteItemId) =>
  getQuoteItemById(state, quoteItemId)?.isModifiable;

export const getQuoteItemBomDraftUpdatedDate = (state, quoteItemId) =>
  getQuoteItemById(state, quoteItemId)?.bomDraftUpdatedDate;

export const getComponent = (state: RootState, componentId: number) =>
  quoteSelectors.getComponentById(state, componentId);

// TODO: consolidate this with above
export const getComponentById = (state, componentId) =>
  getEntity(state, 'quoteItemComponents', componentId);

export const getFinishOps = (state, componentId) => {
  const component = getComponent(state, componentId);
  if (component.finishes && component.finishes.length > 0) {
    return component.finishes;
  } else {
    return component.operations?.filter((operation) => operation.isFinish);
  }
};

export const getBriefComponentById = (state, componentId) =>
  quoteSelectors.getBriefComponentById(state, componentId);

/**
 * Will attempt to find a quoteItemBriefComponent object from the store.
 * If that does not exist, it will attempt to find a quoteItemComponent from the store.
 * The difference here is that a quoteItemComponent contains all of the details for an
 * expanded component (either the root component or a child expanded component).
 * Root components should never have a quoteItemBriefComponent. Child components may or may not
 * have a quoteItemComponent depending on if they have been expanded. The quoteItemBriefComponent
 * will have the most reliable and up to date information for child components.
 * This should only be used to grab information that is shared between the two serializers,
 * notably: quoteItemId, parentComponentIds, isModifiable, and hasNest
 *
 * TODO: type this
 */
const getBriefComponentOrComponentById = (state, componentId) => {
  let component = getBriefComponentById(state, componentId);
  if (!component) {
    component = getComponentById(state, componentId);
  }
  return component;
};

export const getQuoteItemIdFromComponentId = (state, componentId) =>
  getBriefComponentOrComponentById(state, componentId)?.quoteItemId;

export const getIsRootComponentFromComponentId = (state, componentId) => {
  let component = getBriefComponentById(state, componentId);
  if (component) {
    // NOTE: root components should never have a brief component be serialized, but for defensive
    // coding and future proofing, we will also defer to the isRootComponent flag
    return component.isRootComponent;
  }
  component = getComponentById(state, componentId);
  return component?.isRootComponent;
};

export const getQuoteItemFromComponentId = (state, componentId) => {
  const quoteItemId = getQuoteItemIdFromComponentId(state, componentId);
  return getQuoteItemById(state, quoteItemId);
};

export const getRootComponentIdFromComponentId = (state, componentId) => {
  const quoteItem = getQuoteItemFromComponentId(state, componentId);
  return quoteItem?.rootComponent;
};

export const getUnitPricePrecision = (state, componentId) => {
  const rootComponentId = getRootComponentIdFromComponentId(state, componentId);
  const rootComponent = getComponentById(state, rootComponentId);
  return rootComponent?.unitPricePrecision;
};

export const getUser = (state) => sessionSelectors.getUser(state);

export const getIsQuoteItemExpanded = (state, quoteItemId) =>
  getScope(state).expandedQuoteItem == quoteItemId;

export const getOperation = (
  state: RootState,
  operationId: number,
): OperationData | undefined =>
  getEntity(state, 'quoteItemOperations', operationId);

export const getCell = (state: RootState, cellId: number): OperationCellData =>
  getEntity(state, 'quoteItemCells', cellId);

export const getComponentByIdDenormalized = (state, componentId) =>
  denormalize(state, componentId, schema.quoteItemComponent);

export const getCellsForOperation = (
  state: RootState,
  operationId: number,
): OperationCellData[] =>
  Object.keys(getEntities(state, 'quoteItemCells'))
    .map((cellId) => getEntity(state, 'quoteItemCells', cellId))
    .filter((cell) => cell.operationId === operationId);

export const getIsFetchingByFieldName = (state) =>
  getScope(state).isFetchingByFieldName;

export const getHasErrorByFieldName = (state) =>
  getScope(state).hasErrorByFieldName;

export const getIsComponentUpdatePending = (state) =>
  getScope(state).isComponentUpdatePending;

export const getIsMaterialTypeOperationCreating = (state) =>
  path(['isOperationCreatingByType', 'material'], getScope(state));

export const getIsOperationTypeOperationCreating = (state) =>
  path(['isOperationCreatingByType', 'operation'], getScope(state));

export const getAreOtherOverridesUpdating = (state, operationId) => {
  const areOverridesUpdating = getScope(state).areOverridesUpdating;
  for (const [opId, isUpdating] of Object.entries(areOverridesUpdating)) {
    if (isUpdating && opId != operationId) {
      return true;
    }
  }
  return false;
};

export const getIsOperationNextInOverridesCalculationQueue = (
  state,
  operationId,
) => {
  const overridesUpdatingQueue = getScope(state).overridesUpdatingQueue;
  if (overridesUpdatingQueue.length > 0) {
    return overridesUpdatingQueue[0] === operationId;
  }
  return true;
};

export const getOverridesUpdatingQueue = (state) =>
  getScope(state).overridesUpdatingQueue;

export const getAreOverridesUpdatingById = (state, operationId) =>
  path(['areOverridesUpdating', operationId], getScope(state));

export const getIsQuoteItemFetching = (state, quoteItemId) =>
  getScope(state).quoteItemMeta[quoteItemId]?.isFetching;

export const getIsQuoteItemFetchingBriefComponents = (state, quoteItemId) =>
  getScope(state).quoteItemMeta[quoteItemId]?.isFetchingBriefComponents;

export const getIsAddOnCreating = (state) => getScope(state).isAddOnCreating;

export const getIsUpdatingPartFilename = (state, componentId) =>
  pathOr(
    false,
    ['updatingPartFilenames', componentId.toString()],
    getScope(state),
  );

export const getAreAddOnOverridesUpdating = (state, addOnId) =>
  path(['areAddOnOverridesUpdating', addOnId], getScope(state));

// todo delete?
export const getAllComponentProcessingStatuses = (state) =>
  getScope(state).componentProcessingStatuses;

// todo delete?
export const getAllComponentInterrogationStatuses = (state) =>
  getScope(state).componentInterrogationStatuses;

export const getProcessingStatusesByComponentId = (state, componentId) => {
  const componentIdToProcessingStatusesMap =
    getAllComponentProcessingStatuses(state);

  const processingStatuses =
    componentIdToProcessingStatusesMap[(componentId ?? '').toString()];

  return processingStatuses;
};

export const getIsAnyProcessing = (state, componentIds) => {
  const componentIdToProcessingStatusesMap =
    getAllComponentProcessingStatuses(state);
  return componentIds.some((componentId) => {
    const statusInfo =
      componentIdToProcessingStatusesMap[componentId.toString()];
    return (
      statusInfo &&
      isStatusInProgress(statusInfo[PROCESSING_CATEGORIES.PROCESSING].status)
    );
  });
};

export const getAreAnyComponentsProcessing = (state) =>
  getIsAnyProcessing(
    state,
    Object.keys(getAllComponentProcessingStatuses(state)),
  );

export const getComponentInterrogationStatus = (state, componentId) =>
  getAllComponentInterrogationStatuses(state)[(componentId ?? '').toString()];

export const getIsAnyInterrogating = (state, componentIds) => {
  const componentIdToInterrogationStatusesMap =
    getAllComponentInterrogationStatuses(state);
  return componentIds.some((componentId) => {
    const status =
      componentIdToInterrogationStatusesMap[componentId.toString()];
    return status && isStatusInProgress(status.status);
  });
};

export const getBriefComponentsForQuoteItem = (state, quoteItem) => {
  const briefComponents = getBriefComponentsById(
    state,
    quoteItem.componentIds || [],
  );
  return briefComponents.filter((bc) => !!bc);
};

export const getIsQuoteItemComponentsReadyForDownload = (
  state,
  component,
  quoteItem,
) => {
  const { part } = component || {};
  const briefComponents = getBriefComponentsForQuoteItem(state, quoteItem);
  const manualChildrenWithUploadedFile = briefComponents.filter(
    (bc) => !bc.isPrimaryFileGeneratedByPaperless,
  );
  const files = part.partFiles ? part.partFiles : [];
  const allowDownload =
    part.authenticatedDownloadUrl ||
    files.length > 0 ||
    manualChildrenWithUploadedFile.length > 0;
  if (allowDownload) {
    const allStatuses = getAllComponentProcessingStatuses(state);
    for (const component of manualChildrenWithUploadedFile) {
      const statusInfo = allStatuses[component.id.toString()];
      if (statusInfo) {
        const canDownload = !isStatusInProgress(
          statusInfo[PROCESSING_CATEGORIES.PROCESSING].status,
        );
        if (!canDownload) {
          return false;
        }
      }
    }
    return true;
  } else {
    return false;
  }
};

export const getIsComponentInterrogating = (state, componentId) => {
  const status = getComponentInterrogationStatus(
    state,
    (componentId ?? '').toString(),
  );
  return status && isStatusInProgress(status.status);
};

export const getIsComponentGeneratingThumbnail = (state, componentId) => {
  const processingStatuses = getProcessingStatusesByComponentId(
    state,
    componentId,
  );

  return (
    processingStatuses &&
    (isStatusInProgress(
      processingStatuses[PROCESSING_CATEGORIES.THUMBNAIL_CREATION].status,
    ) ||
      isStatusInProgress(
        processingStatuses[PROCESSING_CATEGORIES.PROCESSING].status,
      ))
  );
};

export const getIsRootComponentGeneratingThumbnailFromQuoteItemId = (
  state,
  quoteItemId,
) => {
  const rootComponentId = getRootComponentIdByQuoteItemId(state, quoteItemId);
  return getIsComponentGeneratingThumbnail(state, rootComponentId);
};

export const getEstimatorByQuoteItemId = (state, quoteItemId) => {
  const rootComponentId = getRootComponentIdByQuoteItemId(state, quoteItemId);
  const rootComponent = getComponentById(state, rootComponentId);
  return rootComponent?.estimator;
};

export const getIsThumbnailReadyByComponentId = (
  state,
  componentId,
  readyIfNull,
) => {
  const processingStatuses = getProcessingStatusesByComponentId(
    state,
    componentId,
  );

  const resolution =
    getIsThumbnailReadyFromProcessingStatuses(processingStatuses);
  if (resolution === null && readyIfNull) return true;
  return resolution;
};

export const getExpedite = (state, expediteId) =>
  getEntity(state, 'quoteItemExpedite', expediteId);

export const getIsQuoteItemRefreshing = (state, quoteItemId) =>
  getScope(state).refreshingQuoteItemIds.includes(quoteItemId);

export const getIsQuoteItemUpdating = (state, quoteItemId) => {
  for (const updatingQuoteItemId of getScope(state).updatingQuoteItemIds) {
    if (updatingQuoteItemId == quoteItemId) {
      return true;
    }
  }
  return false;
};

export const getTemporaryQuoteItemIds = (state) =>
  getScope(state).temporaryQuoteItemIds;

export const getTemporaryErpQuoteItemIds = (state) =>
  getScope(state).temporaryErpPartQuoteItemIds;

export const getConvertQuoteFileToQuoteItemTaskGroups = (state) =>
  getScope(state).convertQuoteFileToQuoteItemTaskGroups;

export const getConvertQuoteFileToQuoteItemTasks = (state) =>
  getConvertQuoteFileToQuoteItemTaskGroups(state).flat();

export const getTemporaryConvertQuoteFileToQuoteItemIds = (state) => {
  const tasks = getConvertQuoteFileToQuoteItemTasks(state);
  return tasks.map((task) => task.taskId);
};

export const getIsQuoteItemTemporary = (state, quoteItemId) => {
  // Check if a Quote Item has a uuid value for its id
  // this indicates we have only a partial Quote Item (i.e. temporary)
  const quoteId = quoteSelectors.getQuoteId(state);
  const quoteItem = getQuoteItemById(state, quoteItemId);

  return (
    !!quoteItem &&
    quoteItem.quoteId === quoteId &&
    typeof quoteItem.id === 'string'
  );
};

// TODO: THIS SHOULD BE A SIMPLE HASH MAP TO GRAB THE CURRENT STATUS
export const getQuoteItemLoadingStatus = (state, quoteItemId) => {
  if (getIsQuoteItemTemporary(state, quoteItemId)) {
    return 'Uploading';
  } else if (
    isStatusInProgress(
      pathOr(
        null,
        [PROCESSING_CATEGORIES.PROCESSING, 'status'],
        getProcessingStatusesByComponentId(
          state,
          pathOr(
            null,
            ['rootComponent', 'id'],
            getQuoteItemByIdDenormalized(state, quoteItemId),
          ),
        ),
      ),
    )
  ) {
    return 'Processing';
  } else {
    return null;
  }
};

export const getAreAnyQuoteItemsLoading = (state, quoteItemIds = []) =>
  quoteItemIds.some(
    (quoteItemId) => getQuoteItemLoadingStatus(state, quoteItemId) !== null,
  );

export const getIsBomEditingDisabled = (quoteItemId) => (state) => {
  const isUserReadOnly = getIsUserReadOnly(state);
  const isQuoteItemReadOnly = getIsQuoteItemReadOnly(state, quoteItemId);
  const isModifiable = getIsQuoteItemModifiable(state, quoteItemId);
  const isQuoteItemProcessing = getAreAnyQuoteItemsLoading(state, [
    quoteItemId,
  ]);
  const hasMultiComponentNest = getHasMultiComponentNestByQuoteItemId(
    state,
    quoteItemId,
  );
  return (
    isUserReadOnly ||
    isQuoteItemReadOnly ||
    !isModifiable ||
    isQuoteItemProcessing ||
    hasMultiComponentNest
  );
};

export const getShouldIndicateExtractedBomTables = (quoteItemId) => (state) => {
  const isBomEditingDisabled = getIsBomEditingDisabled(quoteItemId)(state);
  const quoteItem = getQuoteItemById(state, quoteItemId);
  const rootComponent = getComponentById(
    state,
    getRootComponentIdByQuoteItemId(state, quoteItemId),
  );

  let hasChildren;
  if (quoteItem?.componentIds) {
    hasChildren = quoteItem.componentIds.length > 1;
  } else {
    hasChildren = rootComponent?.hasChildren ?? true;
  }

  const quoteItemHasDraftBom = quoteItem?.bomDraftUpdatedDate;
  return !isBomEditingDisabled && !hasChildren && !quoteItemHasDraftBom;
};

export const getComponentIdsToBomRollupRefreshArray = (state) => {
  const idsObject = getScope(state).componentsIdsToBomRollupRefresh;
  return Object.keys(idsObject);
};

export const getQuoteWorkflowSteps = (state) =>
  pathOr([], ['quoteWorkflow', 'steps'], getScope(state));

export const getProfitItem = (
  state: RootState,
  profitItemId: number,
): ProfitItemData | undefined =>
  getEntity(state, 'quoteItemProfitItems', profitItemId);

export const getProfitItemCell = (
  state: RootState,
  profitItemCellId: number,
): ProfitItemCellData | undefined =>
  getEntity(state, 'quoteItemProfitItemCells', profitItemCellId);

export const getIsProfitItemCreating = (state) =>
  getScope(state).isProfitItemCreating;

export const getAreProfitItemOverridesUpdating = (state, profitItemId) =>
  path(['areProfitItemOverridesUpdating', profitItemId], getScope(state));

export const getCellsForProfitItem = (state, profitItemId) =>
  Object.keys(getEntities(state, 'quoteItemProfitItemCells'))
    .map((cellId) => getEntity(state, 'quoteItemProfitItemCells', cellId))
    .filter((cell) => cell.profitItemId === profitItemId);

export const getDiscount = (
  state: RootState,
  discountId: number,
): DiscountData | undefined =>
  getEntity(state, 'quoteItemDiscounts', discountId);

export const getDiscountCell = (
  state: RootState,
  discountCellId: number,
): DiscountCellData | undefined =>
  getEntity(state, 'quoteItemDiscountCells', discountCellId);

export const getIsDiscountCreating = (state) =>
  getScope(state).isDiscountCreating;

export const getAreDiscountOverridesUpdating = (state, discountId) =>
  path(['areDiscountOverridesUpdating', discountId], getScope(state));

export const getCellsForDiscount = (
  state: RootState,
  discountId: number,
): DiscountCellData[] =>
  Object.keys(getEntities(state, 'quoteItemDiscountCells'))
    .map((cellId) => getEntity(state, 'quoteItemDiscountCells', cellId))
    .filter((cell: DiscountCellData) => cell.discountId === discountId);

export const getCellUpdatingMap = (state: RootState) =>
  getScope(state).isCellUpdating;

export const areAnyCellsUpdating = (state: RootState) => {
  for (const cellQueue of Object.values(getScope(state).cellUpdatingQueue)) {
    if (cellQueue.length > 0) return true;
  }
  return false;
};

export const getIsCellNextInCalculationQueue = (state, cellId, quantityId) => {
  const cellUpdatingQueue = getScope(state).cellUpdatingQueue[quantityId] ?? [];
  if (cellUpdatingQueue.length > 0) {
    return cellUpdatingQueue[0] === cellId;
  }
  return true;
};

export const getAreMultipleCellsUpdatingForQuantity = (state, quantityId) => {
  const cellUpdatingQueue = getScope(state).cellUpdatingQueue[quantityId] ?? [];
  return cellUpdatingQueue.length > 1;
};

export const getIsExpandedOrIsRootComponentForIds = (state, componentIds) => {
  const expandedId = getExpandedComponentId(state);
  const briefComponents = getBriefComponentsById(state, componentIds);
  const out = {};
  briefComponents.forEach((briefComponent, index) => {
    if (!briefComponent) {
      // if we do not find a brief component, that means it must be a root, since
      // all child components will be serialized as quoteItemBriefComponents, but
      // root component and only child components that have been expanded will be
      // serialized as a quoteItemComponent in the store
      out[componentIds[index]] = true;
    } else {
      out[componentIds[index]] =
        !!expandedId && expandedId.toString() === briefComponent.id.toString();
    }
  });
  return out;
};

/**
 * Collects all part UUIDs from the current quote's components.
 * Accesses the entity store directly for components and their part references.
 */
export const getPartUuidsForCurrentQuote = createSelector(
  [
    quoteSelectors.getQuoteItems,
    (state) => getEntities(state, 'quoteItemComponents'),
  ],
  (quoteItems, components) => {
    const partUuids: string[] = [];
    for (const quoteItem of quoteItems) {
      const componentIds = quoteItem.componentIds ?? [];
      for (const componentId of componentIds) {
        const component = components[componentId];
        // In normalized form, component.part is a UUID string
        const partUuid = component?.part;
        if (typeof partUuid === 'string' && partUuid) {
          partUuids.push(partUuid);
        }
      }
    }
    return partUuids;
  },
);

/**
 * Checks if any supporting part files on the current quote are mid-upload.
 * Uses isAttachingByPartId from the attach-files saga, scoped to parts on
 * this quote only (unlike the global getAreAnyFilesAttaching).
 * Note: isAttachingByPartId covers both primary and supporting uploads, but
 * primary uploads are redundantly caught by getAreAnyComponentsProcessing.
 */
export const getAreAnySupportingPartFilesAttachingOnCurrentQuote = (state) => {
  const partUuids = getPartUuidsForCurrentQuote(state);
  const attachingByPartId = getIsAttachingByPartId(state);
  return partUuids.some((uuid) => attachingByPartId[uuid]);
};

/**
 * Collects all supporting (non-primary) part file UUIDs from the current quote.
 * Uses `part.supportingFileUuids` which is on BasePartData and always present
 * in the entity store — even from the BAQ headers response (BasePartSerializer).
 * This is critical: `part.partFiles` (the rich objects) is only available after
 * line item expansion, but `supportingFileUuids` survives page refresh.
 */
export const getSupportingPartFileUuidsForCurrentQuote = createSelector(
  [getPartUuidsForCurrentQuote, (state) => getEntities(state, 'parts')],
  (partUuids, partsEntities) => {
    const fileUuids: string[] = [];
    for (const partUuid of partUuids) {
      const part = partsEntities[partUuid];
      if (part?.supportingFileUuids) {
        for (const uuid of part.supportingFileUuids) {
          if (uuid) {
            fileUuids.push(uuid);
          }
        }
      }
    }
    return fileUuids;
  },
);
